import { t } from '../i18n';
import type { AdminGoal } from './admin';

// Pure presentation rules for the goal monitor — shared by the row and its
// tests, so the row component stays a renderer.

/** Status → the modifier class its row wears. One map, so badge and bar agree. */
export const GOAL_STATUS_TONE: Record<AdminGoal['status'], string> = {
  stalled: 'goal--stalled',
  blocked: 'goal--blocked',
  in_progress: 'goal--moving',
  not_started: 'goal--idle',
  finished: 'goal--done',
};

/**
 * How long since anything closed, in the words a person would use — or null
 * when there is nothing to say.
 *
 * A FINISHED goal gets null: "finished, quiet 87 days" invites a reader to treat
 * age as decay, and the API already suppresses `daysSinceProgress` there. This
 * mirrors that rather than relying on it, so a row can never render a staleness
 * warning about work that is done.
 */
export function goalQuietLabel(goal: AdminGoal): string | null {
  if (goal.status === 'finished') return null;
  if (goal.daysSinceProgress === null) return t('goals.quiet_never');
  if (goal.daysSinceProgress === 0) return t('goals.quiet_today');
  return t('goals.quiet_days', { days: String(goal.daysSinceProgress) });
}

/**
 * The words for a goal a percentage cannot describe.
 *
 * `measurable: false` is `total > 1` failing, and that is TWO cases wearing one
 * flag. A milestone with nothing filed against it rendered "Tracked as one issue"
 * on the live board — false about the goal, on the page whose whole claim is that
 * its statements are not. One item means the work exists and does not divide into
 * a percentage; zero means nobody has filed it, and only one of those is begun.
 */
export function goalUnmeasuredLabel(goal: AdminGoal): string {
  return goal.total === 0 ? t('goals.no_items') : t('goals.single_item');
}

/**
 * The board's filters, and what each SET actually is.
 *
 * These are the summary band's own tiles turned into narrows, because the tiles
 * are already the four questions a reader has: what needs me, what is moving,
 * what is done, what is left. A fifth control listing the same categories in a
 * different order would be a second vocabulary for one set of ideas.
 *
 * `waiting` is `blocked` PLUS `stalled`, matching `summarizeGoals`' own
 * `waitingOnYou` — a goal nobody has touched in a month needs a human exactly as
 * much as one labelled `hitl`, and splitting them here would make the tile's
 * number disagree with the rows it reveals.
 *
 * `open` is the one whose tile counts something ELSE: `openItems` is a count of
 * ITEMS, while every filter selects GOALS, so its tile can read 25 and reveal 6
 * rows. That is not reconcilable by making the filter cleverer — they are
 * genuinely different units — so the list states `Showing 6 of 9` whenever a
 * filter is on, and the discrepancy explains itself instead of looking like a
 * bug.
 */
export const GOAL_FILTERS = ['waiting', 'in_progress', 'finished', 'open'] as const;
export type GoalFilterKey = (typeof GOAL_FILTERS)[number];

const MATCHES: Record<GoalFilterKey, (g: AdminGoal) => boolean> = {
  waiting: (g) => g.status === 'blocked' || g.status === 'stalled',
  in_progress: (g) => g.status === 'in_progress',
  finished: (g) => g.status === 'finished',
  open: (g) => g.open > 0,
};

/**
 * Narrow the board. `null` is the whole board — never an empty one.
 *
 * The unfiltered case COPIES rather than handing back the caller's array, so the
 * return value has one owner in every branch: the filtered path already mints a
 * new array, and a function that sometimes returns the query's own data and
 * sometimes a fresh list is one whose result you cannot safely touch without
 * checking which branch you took.
 */
export function filterGoals(
  goals: readonly AdminGoal[],
  filter: GoalFilterKey | null,
): AdminGoal[] {
  return filter === null ? [...goals] : goals.filter(MATCHES[filter]);
}
